import type { TLoansApplyOfferData } from 'shared/types/TLoansService';
import type { TOffer } from 'shared/types/TRankingService';

import type { TCreditFormCommonFields } from 'entities/creditForm';
import {
  Additional,
  Addresses,
  Agreements,
  Auto,
  Contacts,
  CreditPurpose,
  Job,
  Passport,
} from 'entities/creditForm/lib/formatters';
import type { TPledgeAutoFields } from 'entities/pledgeAutoForm';
import { formDataToLoansCustomerPledgeAutoAdapter } from 'entities/pledgeAutoForm';
import type { TPledgeEstateFields } from 'entities/pledgeEstateForm';
import { formDataToLoansCustomerPledgeEstateAdapter } from 'entities/pledgeEstateForm';

export type TAdditionalData = {
  pledgeAutoData?: TPledgeAutoFields;
  pledgeEstateData?: TPledgeEstateFields;
  affId?: string;
  stOrderId?: string;
  stParentId?: string;
  fromAutoSendBanner?: boolean;
  autoSendBannerRequest?: 'manual' | 'auto';
  fromYandexFeed?: boolean;
};

// eslint-disable-next-line complexity
export const offerToApplyOfferDataAdapter = (
  offer: TOffer,
  formData: TCreditFormCommonFields,
  additionalData?: TAdditionalData,
  analytics?: TLoansApplyOfferData['analytics'],
): Pick<TLoansApplyOfferData, 'customer' | 'other' | 'analytics'> => {
  const { pledgeAutoData, pledgeEstateData, ...other } = additionalData || {};

  const {
    category,
    calculatedValues,
    additionalInfo,
    loanGracePeriod,
    loanGracePeriodUnit,
    calcId,
    id,
    productName,
    bankId,
    bankName,
    group,
  } = offer || {};

  const isAutoPledge = !!additionalInfo?.isAutoPledge;
  const isEstatePledge = !!additionalInfo?.isPropertyPledge;

  const creditCalculatedValues =
    offer.calculatedValues && 'rate' in offer.calculatedValues ? offer.calculatedValues : undefined;
  const { perMonth, rate } = creditCalculatedValues || {};

  const auto = new Auto(formData).loans();
  const agreements = new Agreements(formData).profile();
  const passport = new Passport(formData).loans();
  const contacts = new Contacts(formData).loans();
  const creditPurpose = new CreditPurpose(formData).map(calculatedValues);
  const addresses = new Addresses(formData).loans();
  const job = new Job(formData).loans();
  const additional = new Additional(formData).loans();

  const customer: Omit<TLoansApplyOfferData['customer'], 'userId'> = {
    loanGracePeriod,
    loanGracePeriodUnit,
    loanPerMonth: perMonth?.toString(),
    loanRate: rate?.from?.toString(),
    calcId,
    productId: id,
    productName,
    productType: category!,
    organizationId: bankId,
    organizationName: bankName,
    loanPledge: {
      auto: isAutoPledge ? formDataToLoansCustomerPledgeAutoAdapter(pledgeAutoData) : undefined,
      estate: isEstatePledge ? formDataToLoansCustomerPledgeEstateAdapter(pledgeEstateData) : undefined,
    },
    ...job,
    ...addresses,
    ...creditPurpose,
    ...contacts,
    ...passport,
    ...agreements,
    ...additional,
    ...auto,
  };

  return {
    customer: customer as TLoansApplyOfferData['customer'],
    other: { ...(other as TLoansApplyOfferData['other']), groupName: group },
    ...(analytics ? { analytics } : {}),
  };
};
